import React from "react";
import { Table, Button, Tooltip, Card } from "antd";
import useInterviewHooks from "Hooks/EmployeeHooks/useInterviewHooks";
import DashboardLayout from "Pages/EmployeeDashboard/LayoutNavbar/LayoutContainers/DashboardLayout";
import DashboardNavbar from "Pages/EmployeeDashboard/LayoutNavbar/Navbars/DashboardNavbar";
import { JobApplicationStatusEnum } from "Constants/Enums/JobApplicantStatusEnum";
import { JobPostingStatusEnum } from "Constants/Enums/JobPostingStatusEnum";
import ApplicantInterviewRounds from "./ApplicantInterviewRounds";


function ShortListEmployeeForm() {

  const {
    loader,
    employeeContract,
    employeeDetails,
    columns,
    selectedTime,
    employeeData,
    isAllFilesSelected,
    handleSelectedTime,
    handleTimeSelect,
    formatDateTime,
    handleShortlistClick,
    contractAcceptance,
    selectedPdf,
    showNavbar,
    submittedEmployees,
    isContractSubmitted,
    isTimeSubmitted,
    employeeInterview,
    setSelectedTime,
    setShowNavbar,
  } = useInterviewHooks();

  // Per Mudassir (May-20 doc): when the posting is Filled AND the applicant
  // is pre-DocumentSent, the page only shows a polished "role filled" empty
  // state.
  const isFilledClosedState =
    employeeData?.jobPosting?.status === JobPostingStatusEnum.Filled &&
    employeeData?.status < JobApplicationStatusEnum.DocumentSend;

  // Pure informational empty states (no user actions): render OUTSIDE
  // DashboardLayout entirely via position: fixed so the card is truly
  // centered in the viewport instead of inheriting the sidebar offset.
  // - Applied: "Your application has been received" message
  const isAppliedEmptyState =
    !isFilledClosedState &&
    employeeData?.status === JobApplicationStatusEnum.Applied;

  // FeedbackAdded: HR has logged feedback; applicant is now in the
  // "decision pending" window. No action from them — render a polished
  // status page (like Greenhouse / Lever) with a progress timeline so they
  // can see exactly where they are in the pipeline instead of staring at
  // a bare card.
  const isFeedbackAddedEmptyState =
    !isFilledClosedState &&
    employeeData?.status === JobApplicationStatusEnum.FeedbackAdded;

  if (isFeedbackAddedEmptyState) {
    return <FeedbackAddedStatusView employeeData={employeeData} />;
  }

  if (isAppliedEmptyState) {
    return (
      <div
        style={{
          position: "fixed",
          inset: 0,
          background: "#f8fafc",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          padding: "20px",
        }}
      >
        <Card
          title="Interview & Hiring Status"
          className="card-professional"
          style={{
            width: "100%",
            maxWidth: 520,
            borderRadius: "20px",
            boxShadow: "0 8px 24px rgba(0,0,0,0.08)",
          }}
        >
          <div
            style={{
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              textAlign: "center",
              padding: "24px 16px 20px",
              margin: "0 auto",
            }}
          >
            <div
              style={{
                width: 56,
                height: 56,
                borderRadius: "50%",
                background:
                  "radial-gradient(circle at center, #dbeafe 0%, #eff6ff 80%)",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                fontSize: "1.6rem",
                boxShadow: "0 4px 12px rgba(29,78,216,0.16)",
                marginBottom: "16px",
              }}
            >
              📩
            </div>
            <h2
              style={{
                fontSize: "1.5rem",
                fontWeight: 700,
                color: "#1e3a5f",
                margin: 0,
              }}
            >
              Your application has been received
            </h2>
            <p
              style={{
                fontSize: "0.95rem",
                color: "#5b6f8c",
                lineHeight: 1.55,
                marginTop: "10px",
                marginBottom: 0,
                maxWidth: 460,
              }}
            >
              Thank you for applying! The hiring team is reviewing your
              application and we&apos;ll update you here as soon as there&apos;s
              progress.
            </p>
          </div>
        </Card>
      </div>
    );
  }

  // For the closed state, render OUTSIDE DashboardLayout entirely so we get
  // a clean centered viewport without any sidebar/offset interference.
  if (isFilledClosedState) {
    return (
      <div
        style={{
          position: "fixed",
          inset: 0,
          background: "#f8fafc",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          padding: "20px",
        }}
      >
        <Card
          title="Interview & Hiring Status"
          className="card-professional"
          style={{
            width: "100%",
            maxWidth: 520,
            borderRadius: "20px",
            boxShadow: "0 8px 24px rgba(0,0,0,0.08)",
          }}
        >
          <div
            style={{
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              textAlign: "center",
              padding: "24px 16px 20px",
              margin: "0 auto",
            }}
          >
            <div
              style={{
                width: 56,
                height: 56,
                borderRadius: "50%",
                background:
                  "radial-gradient(circle at center, #fed7aa 0%, #fff7ed 80%)",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                fontSize: "1.6rem",
                boxShadow: "0 4px 12px rgba(194,107,44,0.16)",
                marginBottom: "16px",
              }}
            >
              🔒
            </div>
            <h2
              style={{
                fontSize: "1.5rem",
                fontWeight: 700,
                color: "#0f5a2a",
                margin: 0,
              }}
            >
              This role has been filled
            </h2>
            <p
              style={{
                fontSize: "0.95rem",
                color: "#5b6f8c",
                lineHeight: 1.55,
                marginTop: "10px",
                marginBottom: "20px",
                maxWidth: 460,
              }}
            >
              Thank you for your interest in this position. The hiring team
              has closed this posting, so no further actions are needed from
              your side. We&apos;ll keep your profile on file for future
              opportunities that match.
            </p>
            <div
              style={{
                display: "flex",
                gap: 10,
                flexWrap: "wrap",
                justifyContent: "center",
              }}
            >
              <Button
                type="primary"
                onClick={() => (window.location.href = "/applicantPortal")}
                style={{
                  background: "#0f5a2a",
                  borderColor: "#0f5a2a",
                  borderRadius: 40,
                  fontWeight: 600,
                  paddingLeft: 22,
                  paddingRight: 22,
                }}
              >
                View My Applications
              </Button>
              <Button
                onClick={() => (window.location.href = "/viewcareer")}
                style={{
                  borderRadius: 40,
                  fontWeight: 600,
                  paddingLeft: 22,
                  paddingRight: 22,
                  borderColor: "#0f5a2a",
                  color: "#0f5a2a",
                }}
              >
                Browse Other Roles
              </Button>
            </div>
          </div>
        </Card>
      </div>
    );
  }

  return (
    <DashboardLayout>
      <div className="wrapper d-flex flex-column min-vh-100 bg-light">
        {showNavbar && <DashboardNavbar />}
        <div
          className="body flex-grow-1 px-3"
          style={{
            // Escape DashboardLayout's 274px left offset and span the full
            // viewport, then center content inside via flexbox.
            marginLeft: "-274px",
            width: "100vw",
            display: "flex",
            justifyContent: "center",
            padding: "20px 30px",
            boxSizing: "border-box",
          }}
        >
          <Card
            loading={loader}
            title="Interview & Hiring Status"
            className="card-professional"
            style={{
              width: "100%",
              // Square-ish for the Filled closed state so the page doesn't
              // show a giant Card with empty whitespace; wider for the
              // regular pipeline content that fills it naturally.
              maxWidth: isFilledClosedState ? "560px" : "1100px",
              // Let the Card size to its content (no vertical stretch) in
              // closed state.
              height: isFilledClosedState ? "fit-content" : undefined,
              borderRadius: "20px",
              boxShadow: "0 4px 12px rgba(0,0,0,0.06)",
            }}
          >
            {/* Per Mudassir (May-20 doc): when the underlying JobPosting is
                Filled AND the applicant is pre-DocumentSent (no remaining
                action), show a polished full-card "application closed" state
                instead of the noisy pipeline UI. Past DocumentSent (contract
                submit, onboarding) the normal flow renders with a small
                banner so the applicant can still complete their part. */}
            {isFilledClosedState ? (
              <div
                style={{
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "center",
                  textAlign: "center",
                  padding: "24px 16px 20px",
                  maxWidth: 480,
                  margin: "0 auto",
                }}
              >
                <div
                  style={{
                    width: 56,
                    height: 56,
                    borderRadius: "50%",
                    background:
                      "radial-gradient(circle at center, #fed7aa 0%, #fff7ed 80%)",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    fontSize: "1.6rem",
                    boxShadow: "0 4px 12px rgba(194,107,44,0.16)",
                    marginBottom: "16px",
                  }}
                >
                  🔒
                </div>
                <h2
                  style={{
                    fontSize: "1.5rem",
                    fontWeight: 700,
                    color: "#0f5a2a",
                    margin: 0,
                  }}
                >
                  This role has been filled
                </h2>
                <p
                  style={{
                    fontSize: "0.95rem",
                    color: "#5b6f8c",
                    lineHeight: 1.55,
                    marginTop: "10px",
                    marginBottom: "20px",
                    maxWidth: 460,
                  }}
                >
                  Thank you for your interest in this position. The hiring team
                  has closed this posting, so no further actions are needed from
                  your side. We&apos;ll keep your profile on file for future
                  opportunities that match.
                </p>
                <div
                  style={{
                    display: "flex",
                    gap: 10,
                    flexWrap: "wrap",
                    justifyContent: "center",
                    marginBottom: "28px",
                  }}
                >
                  <Button
                    type="primary"
                    onClick={() => (window.location.href = "/applicantPortal")}
                    style={{
                      background: "#0f5a2a",
                      borderColor: "#0f5a2a",
                      borderRadius: 40,
                      fontWeight: 600,
                      paddingLeft: 22,
                      paddingRight: 22,
                    }}
                  >
                    View My Applications
                  </Button>
                  <Button
                    onClick={() => (window.location.href = "/viewcareer")}
                    style={{
                      borderRadius: 40,
                      fontWeight: 600,
                      paddingLeft: 22,
                      paddingRight: 22,
                      borderColor: "#0f5a2a",
                      color: "#0f5a2a",
                    }}
                  >
                    Browse Other Roles
                  </Button>
                </div>

                {/* Applied-on detail strip */}
                {employeeData?.appliedOn && (
                  <div
                    style={{
                      borderTop: "1px solid #e6ede6",
                      paddingTop: "18px",
                      width: "100%",
                      maxWidth: 360,
                      display: "flex",
                      justifyContent: "space-between",
                      fontSize: "0.82rem",
                      color: "#617e9e",
                    }}
                  >
                    <span>Applied on</span>
                    <span style={{ fontWeight: 600, color: "#3e5a3e" }}>
                      {formatDateTime
                        ? formatDateTime(employeeData.appliedOn)
                        : new Date(employeeData.appliedOn).toLocaleDateString()}
                    </span>
                  </div>
                )}
              </div>
            ) : (
              <>
                {/* Past DocumentSent + Filled: small ribbon banner above the
                    normal pipeline content (contract submit + onboarding
                    flows still active per spec). */}
                {employeeData?.jobPosting?.status === JobPostingStatusEnum.Filled && (
                  <div
                    style={{
                      background: "#fff7ed",
                      borderRadius: "10px",
                      padding: "8px 14px",
                      border: "1px solid #fcd9a3",
                      borderLeft: "3px solid #c26b2c",
                      marginBottom: "0.9rem",
                      display: "inline-flex",
                      alignItems: "center",
                      gap: "8px",
                      fontSize: "0.82rem",
                      color: "#7a4a16",
                      maxWidth: "fit-content",
                    }}
                  >
                    <span style={{ fontSize: "0.95rem" }}>🔒</span>
                    <span>
                      <strong>This role has been filled.</strong> Complete your
                      pending steps below.
                    </span>
                  </div>
                )}

            {employeeData?.status === JobApplicationStatusEnum.Applied ? (
              <div
                style={{
                  background: "linear-gradient(135deg, #eff6ff 0%, #dbeafe 100%)",
                  borderRadius: "20px",
                  padding: "24px",
                  borderLeft: "4px solid #1677ff",
                  display: "flex",
                  alignItems: "center",
                  gap: "16px",
                  flexWrap: "wrap",
                }}
              >
                <div style={{ fontSize: "2.4rem" }}>📩</div>
                <div>
                  <div
                    style={{
                      fontSize: "1.1rem",
                      fontWeight: 600,
                      color: "#1e3a5f",
                    }}
                  >
                    Your application has been received
                  </div>
                  <div
                    style={{
                      fontSize: "0.9rem",
                      color: "#1d4ed8",
                      marginTop: "4px",
                    }}
                  >
                    {"Thank you for applying! The hiring team is reviewing your application and we'll update you here as soon as there's progress."}
                  </div>
                </div>
              </div>
            ) : employeeData?.status === JobApplicationStatusEnum.InterviewInProgress ? (
              <ApplicantInterviewRounds
                jobApplicationId={employeeData.jobApplicantId}
                isPostingFilled={
                  employeeData?.jobPosting?.status === JobPostingStatusEnum.Filled
                }
              />
            ) : employeeData?.status === JobApplicationStatusEnum.InterviewCompleted ? (
              <div
                style={{
                  background: "linear-gradient(135deg, #f0fdf4 0%, #ecfdf5 100%)",
                  borderRadius: "20px",
                  padding: "24px",
                  borderLeft: "4px solid #22c55e",
                  display: "flex",
                  alignItems: "center",
                  gap: "16px",
                  flexWrap: "wrap",
                }}
              >
                <div style={{ fontSize: "2.4rem" }}>✅</div>
                <div>
                  <div
                    style={{
                      fontSize: "1.1rem",
                      fontWeight: 600,
                      color: "#166534",
                    }}
                  >
                    All interview rounds completed
                  </div>
                  <div
                    style={{
                      fontSize: "0.9rem",
                      color: "#15803d",
                      marginTop: "4px",
                    }}
                  >
                    {"Thank you for your time! We're now awaiting feedback from the hiring team — we'll be in touch with next steps soon."}
                  </div>
                </div>
              </div>
            ) : (
              <>
            {employeeInterview &&
              Object.keys(employeeInterview).length > 0 &&
              employeeInterview.firstDateTime &&
              !employeeContract.ndafile &&
              !employeeContract.ncafile &&
              !employeeContract.employeeAgreement &&
              !employeeContract.i_nine &&
              !employeeContract.w_four && (
                <div key={employeeInterview.jobApplicantId} className="row">
                  {employeeData.status === JobApplicationStatusEnum.InterviewTimeSchedule ? (
                    // Added fallback condition
                    <>
                      <p style={{ fontSize: "medium" }}>
                        Dear{" "}
                        <strong style={{ color: "#1171ef" }}>
                          {employeeData?.firstName + " " + employeeData?.lastName}
                        </strong>
                        , please select a time for your interview.
                      </p>
                      <div>
                        <input
                          type="radio"
                          id="firstDateTime"
                          name="interviewTime"
                          value={employeeInterview.firstDateTime}
                          onChange={(event) => {
                            setSelectedTime(event.target.value);
                            handleTimeSelect(employeeInterview.jobApplicantId, event);
                          }}
                          style={{ marginRight: "10px" }}
                        />
                        <label htmlFor="firstDateTime" style={{ fontSize: "medium" }}>
                          First Time :: ({formatDateTime(employeeInterview.firstDateTime)})
                        </label>
                      </div>
                      <div>
                        <input
                          type="radio"
                          id="secondDateTime"
                          name="interviewTime"
                          value={employeeInterview.secondDateTime}
                          onChange={
                            (event) => handleTimeSelect(employeeInterview.jobApplicantId, event) // Changed to jobApplicantId
                          }
                          style={{ marginRight: "10px" }}
                        />
                        <label htmlFor="secondDateTime" style={{ fontSize: "medium" }}>
                          Second Time :: ({formatDateTime(employeeInterview.secondDateTime)})
                        </label>
                      </div>
                      <div>
                        <input
                          type="radio"
                          id="thirdDateTime"
                          name="interviewTime"
                          value={employeeInterview.thirdDateTime}
                          onChange={
                            (event) => handleTimeSelect(employeeInterview.jobApplicantId, event) // Changed to jobApplicantId
                          }
                          style={{ marginRight: "10px" }}
                        />
                        <label htmlFor="thirdDateTime" style={{ fontSize: "medium" }}>
                          Third Time :: ({formatDateTime(employeeInterview.thirdDateTime)})
                        </label>
                      </div>
                      <br />
                      <br />
                      <div className="col-md-10">
                        <Button
                          className="btn-primary"
                          onClick={handleSelectedTime}
                          disabled={
                            isTimeSubmitted ||
                            employeeData?.jobPosting?.status ===
                              JobPostingStatusEnum.Filled
                          }
                        >
                          Submit
                        </Button>
                      </div>
                    </>
                  ) : employeeData.status === JobApplicationStatusEnum.FeedbackAdded ? (
                    //  ADD YOUR CODE HERE
                    <div className="col-md-10">
                      <p style={{ fontSize: "medium", color: "green" }}>
                        Your interview feedback has been recorded. Please wait for further updates
                        from the team.
                      </p>
                    </div>
                  ) : (
                    <div className="col-md-10">
                      <p style={{ fontSize: "medium", marginTop: "10px", color: "green" }}>
                        Your interview schedule has been sent to the Datagonomix team. Please wait
                        for the next step.
                      </p>
                    </div>
                  )}
                </div>
              )}
            {!employeeContract.ndafile &&
            !employeeContract.ncafile &&
            !employeeContract.employeeAgreement &&
            !employeeContract.i_nine &&
            !employeeContract.w_four &&
            !employeeContract.offerLetter &&
            (!employeeInterview || !employeeInterview.firstDateTime) ? (
              <div>
                {employeeDetails.map((employee) => (
                  <div key={employee.jobApplicantId} className="row">
                    {employee.status === JobApplicationStatusEnum.ShortList ? (
                      <>
                        {!submittedEmployees[employee.jobApplicantId] ? (
                          <>
                            <div className="col-md-10">
                              <div className="form-group">
                                <p style={{ fontSize: "medium" }}>
                                  Dear{" "}
                                  <strong>
                                    {employee.firstName} {employee.lastName}
                                  </strong>
                                  ,<br />
                                  <br />I hope this message finds you well.<br></br>
                                  <br></br>I am writing to inform you that you have been selected
                                  for an interview for the <strong>{employee.forJobApply}</strong>{" "}
                                  role at Datagonomix on{" "}
                                  <strong>{formatDateTime(employee.createdOn)}</strong>. Your
                                  qualifications and experience have impressed us, and we believe
                                  you would make a valuable addition to our team. To proceed with
                                  the interview process, we kindly request your acceptance of this
                                  invitation. Once confirmed, we will provide you with further
                                  details regarding the next steps and scheduling.
                                </p>
                              </div>
                            </div>
                            <div className="col-md-10">
                              <Button
                                className="btn-primary"
                                onClick={() => handleShortlistClick(employee)}
                                disabled={
                                  submittedEmployees[employee.jobApplicantId] ||
                                  employeeData?.jobPosting?.status ===
                                    JobPostingStatusEnum.Filled
                                }
                              >
                                Submit
                              </Button>
                            </div>
                          </>
                        ) : (
                          <div className="col-md-10">
                            
                          </div>
                        )}
                      </>
                    ) : (
                      <div className="col-md-10">
                        
                      </div>
                    )}
                  </div>
                ))}
              </div>
            ) : null}

            {employeeContract && (
              <div className="row">
                {employeeContract?.status === JobApplicationStatusEnum.Applied ? (
                  <>
                    {!isContractSubmitted ? (
                      <>
                        <div className="row mb-3">
                          <Table
                            className="custom-table"
                            dataSource={[
                              employeeContract.ndafile && {
                                key: "ndafile",
                                name: "NDA File",
                                file: employeeContract.ndafile,
                              },
                              employeeContract.ncafile && {
                                key: "ncafile",
                                name: "NCA File",
                                file: employeeContract.ncafile,
                              },
                              employeeContract.employeeAgreement && {
                                key: "employeeAgreement",
                                name: "Employee Agreement",
                                file: employeeContract.employeeAgreement,
                              },
                              employeeContract.i_nine && {
                                key: "i_nine",
                                name: "I-9 File",
                                file: employeeContract.i_nine,
                              },
                              employeeContract.w_four && {
                                key: "w_four",
                                name: "W-4 File",
                                file: employeeContract.w_four,
                              },
                              employeeContract.offerLetter && {
                                key: "offerLetter",
                                name: "Offer Letter",
                                file: employeeContract.offerLetter,
                              },
                              employeeContract.hrPolicy && {
                                key: "hrPolicy",
                                name: "HR Policy",
                                file: employeeContract.hrPolicy,
                              },
                            ].filter(Boolean)}
                            pagination={false}
                            columns={columns}
                          />
                        </div>

                        <div className="col-md-12">
                          <Tooltip
                            title={!isAllFilesSelected ? "Please select files" : ""}
                            disableHoverListener={isAllFilesSelected || isContractSubmitted}
                            placement="top"
                          >
                            <span>
                              {/* Per Mudassir clarification (May-20): block
                                  hiring pipeline actions only TILL DocumentSent.
                                  The applicant's contract submit (which moves
                                  status DocumentSent → DocumentReceived) is
                                  PAST that cutoff, so it stays enabled even
                                  if the posting is Filled. */}
                              <Button
                                variant="outlined"
                                className="btn-primary"
                                disabled={!isAllFilesSelected || isContractSubmitted}
                                onClick={() => contractAcceptance()}
                              >
                                Submit
                              </Button>
                            </span>
                          </Tooltip>
                        </div>
                      </>
                    ) : (
                      <div className="col-md-12">
                        <p style={{ fontSize: "medium", color: "green" }}>
                          Your files have been sent to the Datagonomix team. The team will provide
                          you an email and new credentials for the employee dashboard.
                        </p>
                      </div>
                    )}
                  </>
                ) : (
                  // Status-specific messages so the page never goes blank.
                  // Per Mudassir: the original "files sent to Datagonomix"
                  // copy was wrong for DocumentRecieved+ — now scoped to
                  // DocumentSend only, with dedicated messages for the
                  // later stages.
                  <div className="col-md-12">
                    {employeeData?.status === JobApplicationStatusEnum.DocumentSend && (
                      <p style={{ fontSize: "medium", color: "green" }}>
                        Your files have been sent to the Datagonomix team. The team will provide you
                        an email and new credentials for the employee dashboard.
                      </p>
                    )}
                    {employeeData?.status === JobApplicationStatusEnum.DocumentRecieved && (
                      <p style={{ fontSize: "medium", color: "green" }}>
                        Your documents have been received. Our team is reviewing them and preparing
                        your onboarding — we&apos;ll be in touch shortly with the next steps.
                      </p>
                    )}
                    {employeeData?.status === JobApplicationStatusEnum.OnboardingDone && (
                      <p style={{ fontSize: "medium", color: "green" }}>
                        Onboarding complete! You&apos;ll receive your employee credentials shortly so
                        you can access the employee dashboard.
                      </p>
                    )}
                    {employeeData?.status === JobApplicationStatusEnum.Hired && (
                      <p style={{ fontSize: "medium", color: "green" }}>
                        Congratulations — you&apos;ve been officially hired. Welcome to the Datagonomix
                        team! Watch your email for next steps.
                      </p>
                    )}
                    {employeeData?.status === JobApplicationStatusEnum.Reject && (
                      <p style={{ fontSize: "medium", color: "#991b1b" }}>
                        Thank you for your interest. Unfortunately, the team has decided not to move
                        forward with your application at this time.
                      </p>
                    )}
                  </div>
                )}
              </div>
            )}
              </>
            )}
              </>
            )}
          </Card>
        </div>
        <div
          className="modal fade"
          id="staticBackdrop"
          data-bs-backdrop="static"
          data-bs-keyboard="false"
          tabIndex="-1"
          aria-labelledby="staticBackdropLabel"
          aria-hidden="true"
        >
          <div className="modal-dialog">
            <div className="modal-content">
              <div className="modal-body">
                <embed src={selectedPdf} type="application/pdf" width="100%" height="450px" />
              </div>
              <div className="modal-footer">
                <button
                  type="button"
                  onClick={() => setShowNavbar(true)}
                  className="btn btn-secondary"
                  data-bs-dismiss="modal"
                >
                  Close
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </DashboardLayout>
  );
}

// =====================================================================
// FeedbackAddedStatusView — polished status page rendered when the
// applicant's pipeline status is FeedbackAdded. No actions — purely
// informational. Modeled on Greenhouse / Lever applicant portals:
// job summary + visual progress timeline + clear "what's next".
// =====================================================================
/* eslint-disable react/prop-types */
function FeedbackAddedStatusView({ employeeData }) {
  const appliedOn =
    employeeData?.appliedOn ||
    employeeData?.createdOn ||
    employeeData?.applicationDate;
  const jobTitle =
    employeeData?.forJobApply ||
    employeeData?.jobPosting?.jobTitle ||
    "Your application";
  const department =
    employeeData?.jobPosting?.departmentName ||
    employeeData?.departmentName ||
    "";
  const location =
    employeeData?.jobPosting?.jobLocationType ||
    employeeData?.jobLocationType ||
    "";

  const formatDate = (iso) => {
    if (!iso) return "—";
    try {
      return new Date(iso).toLocaleDateString("en-US", {
        month: "short",
        day: "numeric",
        year: "numeric",
      });
    } catch {
      return "—";
    }
  };

  // 5-step external applicant timeline. FeedbackAdded = step 4 (decision
  // pending). Current step gets the brand green; earlier steps get the
  // completed checkmark; later steps stay greyed.
  const steps = [
    { key: "applied", label: "Applied", state: "done" },
    { key: "shortlist", label: "Shortlisted", state: "done" },
    { key: "interview", label: "Interview", state: "done" },
    { key: "feedback", label: "Feedback Received", state: "current" },
    { key: "decision", label: "Decision", state: "upcoming" },
  ];

  return (
    <div
      style={{
        position: "fixed",
        inset: 0,
        background: "#f8fafc",
        overflowY: "auto",
        padding: "32px 20px",
        boxSizing: "border-box",
      }}
    >
      <div style={{ maxWidth: 720, margin: "0 auto" }}>
        {/* HEADER — Job summary */}
        <div
          style={{
            background:
              "linear-gradient(135deg, #0f5a2a 0%, #1e8c45 100%)",
            borderRadius: "20px",
            padding: "26px 28px",
            color: "white",
            marginBottom: "20px",
            boxShadow: "0 8px 20px rgba(15,90,42,0.18)",
          }}
        >
          <div
            style={{
              fontSize: "0.72rem",
              textTransform: "uppercase",
              letterSpacing: "0.6px",
              opacity: 0.85,
              fontWeight: 600,
              marginBottom: 6,
            }}
          >
            Application Status
          </div>
          <div
            style={{
              fontSize: "1.6rem",
              fontWeight: 700,
              lineHeight: 1.2,
              marginBottom: 8,
            }}
          >
            {jobTitle}
          </div>
          <div
            style={{
              display: "flex",
              gap: 14,
              flexWrap: "wrap",
              fontSize: "0.86rem",
              opacity: 0.95,
            }}
          >
            {department && <span>🏢 {department}</span>}
            {location && <span>📍 {location}</span>}
            {appliedOn && <span>📅 Applied {formatDate(appliedOn)}</span>}
          </div>
        </div>

        {/* PROGRESS TIMELINE */}
        <div
          style={{
            background: "white",
            borderRadius: "20px",
            padding: "26px 28px 22px",
            marginBottom: "20px",
            boxShadow: "0 4px 12px rgba(0,0,0,0.05)",
            border: "1px solid #e6ede6",
          }}
        >
          <div
            style={{
              fontSize: "0.7rem",
              fontWeight: 700,
              textTransform: "uppercase",
              letterSpacing: "0.6px",
              color: "#617e63",
              marginBottom: 18,
            }}
          >
            Your Progress
          </div>
          <div
            style={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "flex-start",
              position: "relative",
            }}
          >
            {/* Connector line behind dots */}
            <div
              style={{
                position: "absolute",
                top: 14,
                left: "10%",
                right: "10%",
                height: 2,
                background: "#e2e8f0",
                zIndex: 0,
              }}
            />
            <div
              style={{
                position: "absolute",
                top: 14,
                left: "10%",
                width: "60%", // covers up through "feedback" (step 4 of 5)
                height: 2,
                background: "#22c55e",
                zIndex: 0,
              }}
            />
            {steps.map((step) => {
              const isDone = step.state === "done";
              const isCurrent = step.state === "current";
              const dotColor = isDone
                ? "#22c55e"
                : isCurrent
                ? "#0f5a2a"
                : "#cbd5e1";
              const labelColor = isCurrent
                ? "#0f5a2a"
                : isDone
                ? "#15803d"
                : "#94a3b8";
              return (
                <div
                  key={step.key}
                  style={{
                    display: "flex",
                    flexDirection: "column",
                    alignItems: "center",
                    flex: 1,
                    zIndex: 1,
                  }}
                >
                  <div
                    style={{
                      width: 30,
                      height: 30,
                      borderRadius: "50%",
                      background: isCurrent ? "#0f5a2a" : "white",
                      border: `3px solid ${dotColor}`,
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      fontSize: "0.85rem",
                      color: isCurrent ? "white" : dotColor,
                      fontWeight: 700,
                      boxShadow: isCurrent
                        ? "0 0 0 6px rgba(15,90,42,0.12)"
                        : "none",
                    }}
                  >
                    {isDone ? "✓" : isCurrent ? "•" : ""}
                  </div>
                  <div
                    style={{
                      fontSize: "0.78rem",
                      fontWeight: isCurrent ? 700 : 600,
                      color: labelColor,
                      marginTop: 10,
                      textAlign: "center",
                      maxWidth: 100,
                      lineHeight: 1.25,
                    }}
                  >
                    {step.label}
                  </div>
                </div>
              );
            })}
          </div>
        </div>

        {/* STATUS MESSAGE */}
        <div
          style={{
            background:
              "linear-gradient(135deg, #f0fdf4 0%, #ecfdf5 100%)",
            borderRadius: "20px",
            padding: "24px 28px",
            border: "1px solid #d1f5dd",
            borderLeft: "5px solid #22c55e",
            marginBottom: "20px",
            display: "flex",
            gap: 18,
            alignItems: "flex-start",
          }}
        >
          <div style={{ fontSize: "2.2rem", lineHeight: 1 }}>📝</div>
          <div style={{ flex: 1 }}>
            <div
              style={{
                fontSize: "1.15rem",
                fontWeight: 700,
                color: "#0f5a2a",
                marginBottom: 6,
              }}
            >
              Your interview feedback has been recorded
            </div>
            <div
              style={{
                fontSize: "0.92rem",
                color: "#15803d",
                lineHeight: 1.55,
              }}
            >
              Thank you for taking the time to interview with us. Our
              hiring team is now reviewing your evaluation alongside other
              candidates and will reach a decision shortly.
            </div>
          </div>
        </div>

      </div>
    </div>
  );
}

export default ShortListEmployeeForm;
